/**
 * Composant de sélection de groupe dans le créateur de sondage
 * Permet de sélectionner un groupe existant ou de sauvegarder la liste actuelle
 */

import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useGroups, GROUP_PLAN_LIMIT_PREFIX } from "@core/hooks/useGroups";
import { useQuotaModal } from "@core/contexts/QuotaModalContext";
import { Users, Save, ChevronDown, LogIn } from "lucide-react";
import { useToast } from "@core/hooks/use-toast";
import { useAuth } from "@core/contexts/AuthContext";
import { PollData } from "@core/hooks/usePolls";
import {
  PendingPollService,
  PendingPollData,
} from "@core/services/PendingPollService";
import { useNavigate } from "react-router-dom";

interface GroupSelectorProps {
  currentEmails: string[];
  onSelectGroup: (
    emails: string[],
    group?: { id: string; name: string },
  ) => void;
  onSaveAsGroup?: (groupId: string) => void;
  currentPollData?: PollData;
  selectedGroupId?: string;
}

export function GroupSelector({
  currentEmails,
  onSelectGroup,
  onSaveAsGroup,
  currentPollData,
  selectedGroupId,
}: GroupSelectorProps) {
  const { t } = useTranslation("polls");
  const { user } = useAuth();
  const { groups, createGroup, loading } = useGroups();
  const { toast } = useToast();
  const { openQuotaModal } = useQuotaModal();
  const navigate = useNavigate();
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  // Emails du groupe en cours de création (éditable : permet de créer un groupe
  // de zéro, pas seulement de sauvegarder une liste déjà saisie).
  const [newGroupEmails, setNewGroupEmails] = useState("");
  const [saving, setSaving] = useState(false);

  const parseEmails = (raw: string): string[] =>
    raw
      .split(/[,;\n]+/)
      .map((e) => e.trim())
      .filter(Boolean);

  const openSaveDialog = () => {
    // Pré-remplir avec la liste courante si présente (sinon création de zéro).
    setNewGroupEmails(currentEmails.join(", "));
    setShowSaveDialog(true);
  };

  // Fonction de validation d'email avec regex
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Fonction pour valider tous les emails et retourner les erreurs
  const validateEmails = (
    emails: string[],
  ): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];

    emails.forEach((email, index) => {
      if (!validateEmail(email)) {
        if (email.length === 0) {
          errors.push(t("groups.email_error_empty", { index: index + 1 }));
        } else if (!email.includes("@")) {
          errors.push(t("groups.email_error_missing_at", { email }));
        } else if (!email.includes(".")) {
          errors.push(t("groups.email_error_missing_domain", { email }));
        } else if (email.startsWith("@")) {
          errors.push(t("groups.email_error_missing_name", { email }));
        } else if (email.endsWith("@")) {
          errors.push(
            t("groups.email_error_missing_domain_after_at", { email }),
          );
        } else if (email.includes("@@")) {
          errors.push(t("groups.email_error_double_at", { email }));
        } else {
          errors.push(t("groups.email_error_invalid_format", { email }));
        }
      }
    });

    return { isValid: errors.length === 0, errors };
  };

  const handleLoginWithSave = () => {
    if (!currentPollData) {
      toast({
        title: t("groups.no_poll_to_save_title"),
        description: t("groups.no_poll_to_save_description"),
        variant: "destructive",
      });
      return;
    }

    // Déterminer le type de sondage
    const pollType = currentPollData.type || "date";

    const pendingPoll: PendingPollData = {
      pollData: currentPollData,
      timestamp: Date.now(),
      returnPath: window.location.pathname,
      pollType: pollType as "date" | "form" | "quizz" | "availability",
    };

    // Sauvegarder dans sessionStorage
    PendingPollService.savePendingPoll(pendingPoll);

    // Toast rassurant
    toast({
      title: t("groups.poll_saved_title"),
      description: t("groups.poll_saved_description"),
      duration: 3000,
    });

    // Rediriger vers login après 1s
    setTimeout(() => {
      navigate("/login");
    }, 1000);
  };

  const handleSelectGroup = (groupId: string) => {
    const group = groups.find((g) => g.id === groupId);
    if (group) {
      onSelectGroup(group.member_emails, { id: group.id, name: group.name });
      toast({
        title: t("groups.group_selected_title"),
        description: t("groups.group_selected_description", {
          count: group.member_emails.length,
          name: group.name,
        }),
      });
    }
  };

  const handleSaveAsGroup = async () => {
    if (!newGroupName.trim()) {
      toast({
        title: t("groups.name_required_title"),
        description: t("groups.name_required_description"),
        variant: "destructive",
      });
      return;
    }

    const groupEmails = parseEmails(newGroupEmails);
    if (groupEmails.length === 0) {
      toast({
        title: t("groups.no_email_title"),
        description: t("groups.no_email_description"),
        variant: "destructive",
      });
      return;
    }

    // Validation des formats d'email
    const emailValidation = validateEmails(groupEmails);
    if (!emailValidation.isValid) {
      const errorMessage =
        emailValidation.errors.length > 3
          ? t("groups.invalid_emails_summary", {
              count: emailValidation.errors.length,
            })
          : t("groups.invalid_emails_list", {
              errors: emailValidation.errors.join(", "),
            });

      toast({
        title: t("groups.invalid_email_format_title"),
        description: errorMessage,
        variant: "destructive",
      });
      return;
    }

    setSaving(true);
    try {
      const newGroup = await createGroup({
        name: newGroupName.trim(),
        member_emails: groupEmails,
      });

      if (newGroup) {
        toast({
          title: t("groups.group_saved_title"),
          description: t("groups.group_saved_description", {
            name: newGroup.name,
            count: newGroup.member_emails.length,
          }),
        });
        setShowSaveDialog(false);
        setNewGroupName("");
        setNewGroupEmails("");
        // Sélectionne aussitôt le groupe fraîchement créé.
        onSelectGroup(newGroup.member_emails, {
          id: newGroup.id,
          name: newGroup.name,
        });
        onSaveAsGroup?.(newGroup.id);
      }
    } catch (error) {
      // Plan Gratuit atteint : ce n'est pas une panne, c'est un palier. Même
      // traitement que SaveEmailsAsGroupCta (modale de quota), jamais le code
      // technique brut dans un toast.
      if (
        error instanceof Error &&
        error.message.startsWith(GROUP_PLAN_LIMIT_PREFIX)
      ) {
        openQuotaModal("plan_limit", "date");
        return;
      }
      const errorMessage =
        error instanceof Error
          ? error.message
          : t("groups.save_failed_fallback");

      toast({
        title: t("groups.error_title"),
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  // Si l'utilisateur n'est pas connecté, afficher un message d'information
  if (!user) {
    return (
      <div className="p-4 border-2 border-primary/40 rounded-md bg-primary/10">
        <div className="flex items-start gap-3">
          <Save className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <h4 className="text-sm font-medium text-primary mb-1">
              💾 {t("groups.save_groups_title")}
            </h4>
            <p className="text-sm text-primary mb-3">
              {t("groups.save_groups_description")}{" "}
              <strong>{t("groups.save_groups_reassurance")}</strong>
            </p>
            <button
              onClick={handleLoginWithSave}
              className="w-full px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors flex items-center justify-center gap-2 mb-2"
              data-testid="login-to-save-button"
            >
              <LogIn className="w-4 h-4" />
              {t("groups.login_to_save")}
            </button>
            <p className="text-xs text-primary">
              {t("groups.login_to_save_hint")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Sélection d'un groupe existant */}
      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          <Users className="inline w-4 h-4 mr-1" />
          {t("groups.use_existing_group")}
        </label>
        <div className="relative">
          <select
            className="w-full px-3 py-2 pr-10 border border-input rounded-md bg-background text-foreground focus:ring-2 focus:ring-ring focus:border-transparent appearance-none"
            onChange={(e) => {
              if (e.target.value) {
                handleSelectGroup(e.target.value);
              }
            }}
            disabled={loading}
            value={selectedGroupId || ""}
          >
            <option value="">{t("groups.select_group_placeholder")}</option>
            {groups.map((group) => (
              <option key={group.id} value={group.id}>
                {t("groups.group_option", {
                  name: group.name,
                  count: group.member_emails.length,
                })}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
        </div>
        {groups.length === 0 && !loading && (
          <p className="text-sm text-muted-foreground mt-1">
            {t("groups.no_saved_groups")}
          </p>
        )}
      </div>

      {/* Créer un nouveau groupe (nom + emails), de zéro ou depuis la liste courante */}
      {user && (
        <div>
          {!showSaveDialog ? (
            <button
              type="button"
              onClick={openSaveDialog}
              data-testid="group-selector-new-group"
              className="w-full px-4 py-2 border-2 border-dashed border-border rounded-md text-foreground hover:border-primary hover:text-primary transition-colors flex items-center justify-center gap-2"
            >
              <Save className="w-4 h-4" />
              {t("groups.save_as_new_group")}
            </button>
          ) : (
            <div className="p-4 border-2 border-primary/30 rounded-md bg-primary/10 space-y-3">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  {t("groups.group_name_label")}
                </label>
                <input
                  type="text"
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  placeholder={t("groups.group_name_placeholder")}
                  data-testid="group-selector-new-name"
                  className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus:ring-2 focus:ring-ring focus:border-transparent"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  {t("groups.group_emails_label")}
                </label>
                <textarea
                  value={newGroupEmails}
                  onChange={(e) => setNewGroupEmails(e.target.value)}
                  placeholder={t("groups.group_emails_placeholder")}
                  data-testid="group-selector-new-emails"
                  rows={3}
                  className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus:ring-2 focus:ring-ring focus:border-transparent"
                />
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleSaveAsGroup}
                  disabled={saving}
                  data-testid="group-selector-save"
                  className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {saving ? t("groups.saving") : t("groups.save")}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowSaveDialog(false);
                    setNewGroupName("");
                    setNewGroupEmails("");
                  }}
                  disabled={saving}
                  className="px-4 py-2 border border-input text-foreground rounded-md hover:bg-accent transition-colors"
                >
                  {t("groups.cancel")}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
